import React from "react";
import ReactSelect, { MultiValue, SingleValue, StylesConfig } from "react-select";

interface ISelect {
  id: string;
  className?: string;
  label: string;
  options: {
    name: string;
    value: string;
    disabled?: boolean;
  }[];
  readonly?: boolean;
  value?: string | string[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement> | any) => void;
  textColor?: string;
  multiple?: boolean;
  disabled?: boolean;
}

export function Select({
  id,
  className = "",
  label,
  options,
  value,
  onChange,
  readonly = false,
  textColor = "text-dark-blue",
  multiple = false,
  disabled = false,
}: ISelect) {
  return (
    <div className={className}>
      <label className="text-gray-600 text-sm font-medium flex-shrink-0 block" htmlFor={id}>
        {label}
      </label>
      {!readonly ? (
        <select
          multiple={multiple}
          id={id}
          className="w-full p-2 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
          value={value}
          onChange={(e) => {
            onChange(e);
          }}
          disabled={disabled}
        >
          {options.map((e, i) => {
            return (
              <option key={i} value={e.value} disabled={e.disabled}>
                {e.name}
              </option>
            );
          })}
        </select>
      ) : (
        <p className={"text-lg " + textColor}>{value}</p>
      )}
    </div>
  );
}

export interface SelectPlusOptionType<T = any> {
  label: any;
  value: any;
  option?: T;
}

interface ISelectPlus {
  id?: string;
  className?: string;
  label?: React.ReactNode;
  options: SelectPlusOptionType[];
  readonly?: boolean;
  value?: SelectPlusOptionType | SelectPlusOptionType[];
  onChange?: (_e: MultiValue<SelectPlusOptionType> | SingleValue<SelectPlusOptionType>) => void;
  textColor?: string;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  renderValues?: (_values: SelectPlusOptionType | SelectPlusOptionType[]) => React.ReactNode;
  onSearch?: (_e: string) => void;
  isSearchable?: boolean;
  filterOption?: any;
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  noOptionsMessage?: ((obj: { inputValue: string }) => React.ReactNode) | undefined;
  ref?: React.MutableRefObject<any>;
  required?: boolean;
  isClearable?: boolean;
  /** Extra react-select style overrides, merged over the defaults. */
  styles?: StylesConfig<SelectPlusOptionType, boolean>;
}

export function SelectPlus({
  id,
  className = "",
  label,
  options,
  value,
  onChange,
  readonly = false,
  textColor = "text-dark-blue",
  multiple = false,
  disabled = false,
  placeholder,
  renderValues,
  onSearch,
  isSearchable,
  filterOption = undefined,
  loading = false,
  noOptionsMessage,
  ref,
  required = false,
  isClearable = true,
  styles,
}: ISelectPlus) {
  return (
    <div className={className}>
      <label className="text-gray-500  text-sm flex-shrink-0" htmlFor={id}>
        {label}
        {required && <span className="text-red-500"> * </span>}
      </label>
      {!readonly ? (
        <ReactSelect
          ref={ref}
          isLoading={loading}
          options={options}
          isMulti={multiple}
          id={id}
          value={value}
          onChange={(e) => {
            if (onChange) onChange(e);
          }}
          isDisabled={disabled}
          placeholder={placeholder}
          isClearable={isClearable}
          onInputChange={(e) => {
            if (onSearch) onSearch(e);
          }}
          isSearchable={isSearchable}
          filterOption={filterOption}
          noOptionsMessage={noOptionsMessage}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            ...styles,
          }}
        />
      ) : renderValues ? (
        renderValues(value ? value : [])
      ) : value ? (
        Array.isArray(value) ? (
          value.map((v) => <p className={"text-lg " + textColor}>{v.label}</p>)
        ) : (
          <p className={"text-lg " + textColor}>{value.label}</p>
        )
      ) : (
        "-"
      )}
    </div>
  );
}
